import { Log } from '@agilelab/plugin-wb-platform';
import {
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import { JsonObject } from '@mainstage/types';
import { PropTypes } from '@material-ui/core';
import { FormProps } from '@rjsf/core';
import { ErrorSchema } from '@rjsf/utils';
import { WmUiSchema } from '../../extensions/types';
import {
  deserializeEnvironmentParamKey,
  isEnvironmentParamKey,
} from '@agilelab/plugin-wb-platform-common';

export const DEPENDENCY_MANAGER_ID_SEPARATOR = '\uE000';

export type Step = {
  // schema and uiSchema merged into a single object, how templates are written
  mergedSchema: JsonObject;
  schema: JsonObject;
  uiSchema?: JsonObject;
  validator: FormProps<any>['validator'];
  title: string;
  customValidate?: FormProps<any>['customValidate'];
  asyncValidate?: (formData: any) => Promise<ErrorSchema>;
} & Partial<Omit<FormProps<any>, 'schema'>>;

export type AdditionalStep = {
  title: string;
  component: JSX.Element;
  additionalActions?: JSX.Element[];
  backButtonText?: string;
  nextButtonText?: string;
  disableStepButtons: boolean;
  customDisableStepButtons?: {
    disableNext?: boolean;
    disableReset?: boolean;
    disableBack?: boolean;
  };
  analyticsEvent?: string;
  backAction?: () => void;
  nextAction?: () => Promise<void>;
  resetButtonText?: string;
  resetAction?: () => void;
  resetButtonVariant?: 'text' | 'outlined' | 'contained';
  resetButtonColor?: PropTypes.Color;
  taskFailed?: boolean;
  stayOnStep?: boolean;
  onSaveButtonClick?: (params: {
    setStep: (step: number) => void;
    setAdditionalStep: (step: number) => void;
  }) => void;
};

export type Logs =
  | {
      type: 'text-logs';
      text: string;
    }
  | {
      type: 'structured-logs';
      logs: Log[];
    };

function parseEntityRef(entityLike: any): string {
  try {
    return stringifyEntityRef(getCompoundEntityRef(entityLike));
  } catch (error) {
    return '<no name>';
  }
}

export function getReviewData(formData: Record<string, any>, steps: Step[]) {
  const uiSchemas = getUiSchemasFromSteps(steps);

  const reviewData: Record<string, any> = {};

  uiSchemas.forEach(uiSchema => {
    const key = uiSchema.name;
    const value = formData[key];

    if (Array.isArray(value) && value.length === 0) return;

    const processedValue = processValueByUiSchema(value, uiSchema);

    if (processedValue === null || processedValue === undefined) return;

    pushToReviewData(reviewData, key, processedValue, uiSchema);
  });

  return reviewData;
}

function processValueByUiSchema(value: any, uiSchema?: any): any {
  if (!uiSchema) return value;

  // Hide fields marked as hidden in ui:widget
  if (uiSchema['ui:widget'] === 'hidden') {
    return null;
  }

  if (uiSchema['ui:widget'] === 'password') {
    return '******';
  }

  if (uiSchema['ui:field']?.toString().toLowerCase() === 'entitypicker') {
    return value && typeof value === 'string'
      ? value
      : `<descriptor> ${parseEntityRef(value)}`;
  }

  if (uiSchema['ui:field']?.toString().toLowerCase() === 'filepicker') {
    return `File: ${value?.fileName ?? 'File'}`;
  }

  if (uiSchema['ui:field']?.toString().toLowerCase() === 'descriptorpicker') {
    return `${value?.label}`;
  }

  if (uiSchema['ui:backstage']?.review) {
    const review = uiSchema['ui:backstage'].review as JsonObject;

    if (review.mask) {
      return review.mask;
    }

    if (!review.show) {
      return null;
    }
  }

  // Recursively process nested objects
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    uiSchema.properties
  ) {
    const result: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const subSchema = uiSchema.properties[k];
      const processed = processValueByUiSchema(v, subSchema);
      if (processed !== null && processed !== undefined) {
        const displayKey = subSchema?.title ?? k;
        result[displayKey] = processed;
      }
    }
    return result;
  }

  // Recursively process arrays
  if (Array.isArray(value) && uiSchema.items) {
    return value.map(item => processValueByUiSchema(item, uiSchema.items));
  }

  return value;
}

function pushToReviewData(
  reviewData: Record<string, any>,
  key: string,
  processedValue: any,
  uiSchema?: any,
) {
  const groupKey = uiSchema?.env;
  const displayKey = uiSchema?.title ?? key;

  const deserializedValue = deserializeEnvironmentKeysDeep(processedValue);

  if (groupKey) {
    if (!reviewData[groupKey]) {
      reviewData[groupKey] = {};
    }
    reviewData[groupKey][displayKey] = deserializedValue;
  } else {
    reviewData[displayKey] = deserializedValue;
  }
}

function deserializeEnvironmentKeysDeep(value: any): any {
  if (Array.isArray(value)) {
    return value.map(item => deserializeEnvironmentKeysDeep(item));
  }

  if (typeof value === 'object' && value !== null) {
    const result: Record<string, any> = {};

    for (const [key, val] of Object.entries(value)) {
      let newKey = key;
      if (isEnvironmentParamKey(key)) {
        const { key: deserialized } = deserializeEnvironmentParamKey(key);
        newKey = deserialized;
      }

      result[newKey] = deserializeEnvironmentKeysDeep(val);
    }

    return result;
  }

  return value;
}

export function collectPropertiesFromSchema(
  schema: JsonObject,
  collected: JsonObject,
): void {
  const properties = schema.properties as JsonObject | undefined;
  if (properties && typeof properties === 'object') {
    for (const key in properties) {
      if (properties.hasOwnProperty(key) && !collected.hasOwnProperty(key)) {
        collected[key] = properties[key];
      }
    }
  }

  const anyOf = schema.anyOf as JsonObject[] | undefined;
  if (Array.isArray(anyOf)) {
    for (const schemaNode of anyOf) {
      if (!schemaNode || typeof schemaNode !== 'object') continue;
      collectPropertiesFromSchema(schemaNode as JsonObject, collected);
    }
  }

  const oneOf = schema.oneOf as JsonObject[] | undefined;
  if (Array.isArray(oneOf)) {
    for (const schemaNode of oneOf) {
      if (!schemaNode || typeof schemaNode !== 'object') continue;
      collectPropertiesFromSchema(schemaNode as JsonObject, collected);
    }
  }

  const allOf = schema.allOf as JsonObject[] | undefined;
  if (Array.isArray(allOf)) {
    for (const schemaNode of allOf) {
      if (!schemaNode || typeof schemaNode !== 'object') continue;
      if (
        schemaNode.if &&
        schemaNode.then &&
        typeof schemaNode.then === 'object'
      ) {
        collectPropertiesFromSchema(schemaNode.then as JsonObject, collected);
        if (schemaNode.else && typeof schemaNode.else === 'object') {
          collectPropertiesFromSchema(schemaNode.else as JsonObject, collected);
        }
      } else {
        collectPropertiesFromSchema(schemaNode as JsonObject, collected);
      }
    }
  }

  const dependencies = schema.dependencies as JsonObject | undefined;
  if (dependencies && typeof dependencies === 'object') {
    for (const depName of Object.keys(dependencies)) {
      const depSchema = dependencies[depName];
      if (depSchema && typeof depSchema === 'object') {
        collectPropertiesFromSchema(depSchema as JsonObject, collected);
      }
    }
  }
}

export function getUiSchemasFromSteps(steps: Step[]): any[] {
  const uiSchemas: Array<any> = [];
  steps.forEach(step => {
    const mergedSchema = step.mergedSchema;
    const areEnvironmentParameters = mergedSchema.environmentParameters;

    const allProperties: JsonObject = {};
    collectPropertiesFromSchema(mergedSchema, allProperties);

    for (const key in allProperties) {
      if (allProperties.hasOwnProperty(key)) {
        const originalUiSchema =
          allProperties[key] && typeof allProperties[key] === 'object'
            ? allProperties[key]
            : {};

        const uiSchema: WmUiSchema = {
          ...originalUiSchema,
          name: key,
          ...(areEnvironmentParameters && {
            env: deserializeEnvironmentParamKey(key).env,
          }),
        };

        uiSchemas.push(uiSchema);
      }
    }
  });

  return uiSchemas;
}

/**
 * Truncate large arrays in review data to prevent rendering freeze
 */
export function truncateLargeArrays(data: any, maxItems: number): any {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    if (data.length > maxItems) {
      return {
        __isArrayTruncated: true,
        __totalLength: data.length,
        __items: data.slice(0, maxItems),
        __allItems: data,
      };
    }
    return data.map(item => truncateLargeArrays(item, maxItems));
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    result[key] = truncateLargeArrays(value, maxItems);
  }
  return result;
}
